/**
 * Assessment 3 of the saya yield engine — the **zarf (free envelope)**, per
 * material.
 *
 * ── The assessment ──────────────────────────────────────────────────────────
 *
 * The strip (`strip.ts`) answers "how much of our standard-width strip does a
 * set consume". The zarf answers the question the factory reads per material:
 * nest ONE PAIR's pieces of that material freely — no imposed strip width —
 * and report the TIGHT bounding envelope around what was laid. "One çift of
 * this material needs widthMm × lengthMm", converted to the purchase unit by
 * the caller.
 *
 *     gross_area = widthMm × lengthMm        (the occupied bounding box)
 *     η_zarf     = Σ A_net(placed) / gross_area
 *
 * "Free" still honours the material's direction rule: the caller passes the
 * same rotation set its strip run uses, so a napped leather's envelope is
 * searched under exactly the constraints its strip is.
 *
 * ── The search ──────────────────────────────────────────────────────────────
 *
 * There is deliberately NO new packer here. The validated bottom-left placer
 * (`stripPlaceVariant`, `repeats: 1`) is run over a small DETERMINISTIC set of
 * candidate strip widths derived from the set's own net area — √ΣA_net times
 * the fixed multipliers below, rounded to whole mm and deduped — plus the
 * narrowest width the widest instance can present, so at least one candidate
 * fits anything that can be rasterised at all. A candidate some instance still
 * cannot fit (`StripWidthError`) is skipped, not fatal. Each candidate is
 * packed once per feeding order in the reduced variant set below, so the search
 * scores every (width × ordering) run.
 *
 * Every one of those runs is scored HERE, on the OCCUPIED bounding box of its
 * placements, measured from the placed rings themselves — never from `widthMm ×
 * usedLengthMm`, because trailing unused strip width must not count against the
 * envelope. That is also why the search cannot delegate the choice to
 * `stripPlaceBest`: its objective is the shortest STRIP, which is the right
 * answer for a strip and the wrong one here — two orderings reaching the same
 * used length can occupy visibly different boxes, and the shortest-strip tie
 * would discard the tighter one.
 *
 * The smallest box wins; ties break toward the smaller candidate width, then
 * the earlier ordering. Placements are then normalized toward the origin by a
 * whole number of CELLS, and what the box's origin still is inside that frame
 * is reported as `originMm`. The whole thing is a pure function of its inputs:
 * same instances, same options, same envelope, every run.
 */

import {
  DEFAULT_CELL_MM,
  type BBox,
  type Part,
  bbox,
  partNetArea,
  rotate,
  translate,
} from './geometry'
import {
  StripWidthError,
  stripOrderingVariants,
  stripPlaceVariant,
  type Placement,
  type StripOrdering,
} from './strip'

/* ── Public contract ─────────────────────────────────────────────────────── */

/**
 * Version stamp of the envelope assessment, persisted alongside its results.
 * `zarf-3`: the candidate search scores every (width × feeding order) run by
 * its own OCCUPIED BBOX AREA instead of delegating the choice to
 * `stripPlaceBest`'s shortest-strip objective, and the placer's rotation
 * tie-break now measures the skyline raise from occupied rows — both move the
 * envelope.
 */
export const ENVELOPE_ALGO_VERSION = 'zarf-3'

/**
 * The feeding orders the candidate search tries per width. Reduced to two —
 * as-given and longest-diagonal-first — because the work here is already
 * multiplied by the candidate widths: the full four-ordering set would put
 * the zarf at ~4× its previous cost for a figure the strip already carries
 * most of, while these two catch the layouts the single fixed order missed.
 */
const ENVELOPE_ORDERINGS: readonly StripOrdering[] = ['as-given', 'diagonal']

/**
 * Fixed multipliers applied to √ΣA_net to derive the candidate strip widths.
 * Fixed rather than adaptive for the same reason the strip's repeat count is:
 * a reproducible number beats a marginally better one nobody can re-derive.
 */
export const ENVELOPE_WIDTH_MULTIPLIERS: readonly number[] = [0.7, 0.85, 1.0, 1.2, 1.45, 1.75]

export interface EnvelopeOptions {
  /** Cutter clearance between pieces, mm. */
  gapMm: number
  /** Assessment grid resolution, mm. Defaults to `DEFAULT_CELL_MM` (1 mm). */
  cellMm?: number
  /** Rotations to try per instance, degrees CCW, in preference order. */
  rotationsDeg: number[]
}

export interface EnvelopeResult {
  /** Occupied bounding-box width, mm — the tight envelope, not the packing width. */
  widthMm: number
  /** Occupied bounding-box length, mm. */
  lengthMm: number
  /**
   * The candidate strip width the winning layout was packed at. The normalized
   * `placements` still satisfy `validateLayout` against THIS width — the
   * normalization only shifts everything toward the origin by one common
   * whole-cell offset, which cannot leave the strip or close a gap.
   */
  packWidthMm: number
  /** `widthMm × lengthMm`, mm² — the tight envelope's area. */
  grossAreaMm2: number
  /** `Σ A_net(placed) / grossAreaMm2`. */
  efficiency: number
  /**
   * Where the tight envelope starts inside the normalized frame, `[x, y]` mm,
   * each component in `[0, cellMm)`. Normalization moves the placements by a
   * whole number of cells and no further: an exact shift by the box origin
   * would take every piece OFF the assessment lattice — scan coordinates and
   * 15° rotations put that origin at a fraction of a cell — and a re-rasterised
   * layout that no longer lands on the placer's own cells can report clearance
   * collisions that the layout does not have. So the sub-cell remainder is
   * reported rather than removed: the envelope a drawing must frame is
   * `originMm` to `originMm + [widthMm, lengthMm]`, not `(0, 0)` to that.
   */
  originMm: [number, number]
  /** Normalized toward the origin by whole cells — see `originMm`. */
  placements: Placement[]
  algoVersion: string
}

/* ── Placement ───────────────────────────────────────────────────────────── */

export function envelopePlace(instances: Part[], opts: EnvelopeOptions): EnvelopeResult {
  if (instances.length === 0) throw new Error('envelopePlace: no instances')
  const cellMm = opts.cellMm ?? DEFAULT_CELL_MM
  if (!(cellMm > 0)) throw new Error('envelopePlace: cellMm must be > 0')
  if (opts.rotationsDeg.length === 0) {
    throw new Error('envelopePlace: rotationsDeg must list at least one rotation')
  }

  let netAreaMm2 = 0
  for (const instance of instances) netAreaMm2 += partNetArea(instance)

  const candidates = candidateWidthsMm(instances, opts.rotationsDeg, cellMm, netAreaMm2)
  // The feeding orders do not depend on the width, so they are derived once.
  const variants = stripOrderingVariants(instances, ENVELOPE_ORDERINGS)

  // Candidates come sorted ascending, variants come in `ENVELOPE_ORDERINGS`
  // order, and the comparison below is STRICT — so a tie on box area keeps the
  // smaller candidate width, and within one width the earlier ordering.
  let best: { packWidthMm: number; box: BBox; placements: Placement[] } | null = null
  let lastRefusal: StripWidthError | null = null
  for (const packWidthMm of candidates) {
    for (const perm of variants) {
      let placed
      try {
        placed = stripPlaceVariant(
          instances,
          {
            widthMm: packWidthMm,
            gapMm: opts.gapMm,
            cellMm,
            rotationsDeg: opts.rotationsDeg,
            repeats: 1,
          },
          perm,
        )
      } catch (error: unknown) {
        // A candidate too narrow for some instance is skipped, not fatal — the
        // min-width fallback candidate keeps this loop from ending empty for any
        // set the placer can lay at all. A `DegenerateInstanceError` arrives here
        // as the same class and is skipped the same way; it is width-independent,
        // so it refuses every candidate and the loop ends empty by design.
        //
        // The refusal is a per-instance fact, independent of the feeding order,
        // so the remaining variants of THIS width would only reproduce it.
        if (error instanceof StripWidthError) {
          lastRefusal = error
          break
        }
        throw error
      }
      const box = occupiedBBox(placed.placements, instances)
      if (best === null || boxArea(box) < boxArea(best.box)) {
        best = { packWidthMm, box, placements: placed.placements }
      }
    }
  }

  if (best === null) {
    // Every candidate refused — a degenerate instance the placer cannot land
    // anywhere. Surface the placer's own complaint rather than inventing one.
    throw lastRefusal ?? new Error('envelopePlace: no candidate width could be packed')
  }

  const box = best.box
  const widthMm = box.maxX - box.minX
  const lengthMm = box.maxY - box.minY
  const grossAreaMm2 = widthMm * lengthMm
  // The shift is floored to the cell lattice — the same `floor(min / cellMm)`
  // frame `ringToRaster` builds — so every placement stays on the cells it was
  // laid on. Both components are >= 0 and no larger than the box origin, so the
  // shifted layout still sits inside the strip it was packed in.
  const shiftX = Math.floor(box.minX / cellMm) * cellMm
  const shiftY = Math.floor(box.minY / cellMm) * cellMm
  return {
    widthMm,
    lengthMm,
    packWidthMm: best.packWidthMm,
    grossAreaMm2,
    efficiency: grossAreaMm2 > 0 ? netAreaMm2 / grossAreaMm2 : 0,
    originMm: [box.minX - shiftX, box.minY - shiftY],
    placements: best.placements.map((placement) => ({
      index: placement.index,
      xMm: placement.xMm - shiftX,
      yMm: placement.yMm - shiftY,
      rotDeg: placement.rotDeg,
    })),
    algoVersion: ENVELOPE_ALGO_VERSION,
  }
}

/* ── Internals ───────────────────────────────────────────────────────────── */

/**
 * The deterministic candidate list: √ΣA_net × the fixed multipliers, rounded
 * to whole mm, deduped, plus `minFeasibleWidthMm` so the list always contains
 * a width every instance fits. Sorted ascending — the tie-break relies on it.
 */
function candidateWidthsMm(
  instances: Part[],
  rotationsDeg: number[],
  cellMm: number,
  netAreaMm2: number,
): number[] {
  const base = Math.sqrt(netAreaMm2)
  const widths = new Set<number>()
  for (const multiplier of ENVELOPE_WIDTH_MULTIPLIERS) {
    const widthMm = Math.round(base * multiplier)
    if (widthMm >= 1) widths.add(widthMm)
  }
  widths.add(minFeasibleWidthMm(instances, rotationsDeg, cellMm))
  return [...widths].sort((a, b) => a - b)
}

/**
 * The narrowest whole-mm strip every instance fits at SOME allowed rotation:
 * the maximum over instances of the minimum over rotations of the rasterised
 * width. The cell arithmetic mirrors `ringToRaster`'s frame — `ceil(maxX) −
 * floor(minX)` in cells — so a strip of this width never rounds a piece out.
 */
function minFeasibleWidthMm(instances: Part[], rotationsDeg: number[], cellMm: number): number {
  let widestCells = 0
  for (const instance of instances) {
    let narrowestCells = Infinity
    for (const deg of rotationsDeg) {
      const box = bbox(rotate(instance.outer, deg))
      const cells = Math.ceil(box.maxX / cellMm) - Math.floor(box.minX / cellMm)
      if (cells < narrowestCells) narrowestCells = cells
    }
    if (narrowestCells > widestCells) widestCells = narrowestCells
  }
  return Math.max(1, Math.ceil(widestCells * cellMm))
}

/**
 * The tight bounding box of what was actually laid, measured from the placed
 * rings — each placement re-transformed exactly the way `validateLayout`
 * re-transforms it, so the box bounds what the verifier would see.
 */
function occupiedBBox(placements: Placement[], instances: Part[]): BBox {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const placement of placements) {
    const instance = instances[placement.index] as Part
    const ring = translate(rotate(instance.outer, placement.rotDeg), placement.xMm, placement.yMm)
    const box = bbox(ring)
    if (box.minX < minX) minX = box.minX
    if (box.minY < minY) minY = box.minY
    if (box.maxX > maxX) maxX = box.maxX
    if (box.maxY > maxY) maxY = box.maxY
  }
  if (!Number.isFinite(minX)) throw new Error('occupiedBBox: no placements')
  return { minX, minY, maxX, maxY }
}

function boxArea(box: BBox): number {
  return (box.maxX - box.minX) * (box.maxY - box.minY)
}
